import { Website, WebsiteLocation } from "@/data/mockWebsites";

interface CityWebsiteRaw {
  id: number;
  name: string;
  url: string;
  description: string;
  pros: string[];
  cons: string[];
  rating: number;
  category: string;
}

export interface CityConfig {
  name: string;
  center: [number, number]; // [lng, lat]
}

// Known HQ coordinates for national/international companies [lng, lat]
const knownLocations: Record<string, [number, number]> = {
  "immoscout24": [13.4311, 52.5126], // Berlin
  "immobilienscout24": [13.4311, 52.5126],
  "immowelt": [11.0824, 49.4389], // Nürnberg
  "immonet": [9.9937, 53.5511], // Hamburg
  "kleinanzeigen": [13.3888, 52.5200], // Berlin
  "jll": [8.6724, 50.1109], // Frankfurt
  "jones lang lasalle": [8.6724, 50.1109],
  "colliers": [8.6821, 50.1155], // Frankfurt
  "colliers international": [8.6821, 50.1155],
  "bnp paribas real estate": [8.6697, 50.1115],
  "engel & völkers": [9.9814, 53.5575], // Hamburg
  "engel & völkers commercial": [9.9814, 53.5575],
  "savills": [8.6750, 50.1100],
  "cushman & wakefield": [6.7735, 51.2277], // Düsseldorf
  "cbre": [8.6830, 50.1120],
};

// Generate a deterministic spread position around a city center
function spreadAroundCenter(
  center: [number, number],
  index: number,
  total: number
): [number, number] {
  const angle = (index / Math.max(total, 1)) * 2 * Math.PI + (index * 0.7);
  const radius = 0.015 + (index % 5) * 0.008; // ~1-4km spread
  return [
    center[0] + radius * Math.cos(angle),
    center[1] + radius * Math.sin(angle),
  ];
}

function getLocationForSite(
  site: CityWebsiteRaw,
  cityConfig: CityConfig,
  index: number,
  total: number
): WebsiteLocation {
  const nameLower = site.name.toLowerCase();

  // Check if it's a known national/international company
  for (const [key, coords] of Object.entries(knownLocations)) {
    if (nameLower.includes(key) || key.includes(nameLower)) {
      return {
        type: "country",
        name: "Deutschland",
        coordinates: coords,
      };
    }
  }

  // Local businesses get spread around the city center
  const coords = spreadAroundCenter(cityConfig.center, index, total);
  return {
    type: "city",
    name: cityConfig.name,
    coordinates: coords,
  };
}

export function adaptCityWebsites(
  raw: CityWebsiteRaw[],
  cityConfig?: CityConfig
): Website[] {
  return raw.map((w, index) => ({
    id: String(w.id),
    name: w.name,
    url: w.url,
    description: w.description,
    pros: w.pros.join("; "),
    cons: w.cons.join("; "),
    rating: w.rating,
    category: w.category,
    location: cityConfig
      ? getLocationForSite(w, cityConfig, index, raw.length)
      : undefined,
  }));
}

// City center coordinates for all supported cities
export const CITY_CONFIGS: Record<string, CityConfig> = {
  berlin: { name: "Berlin", center: [13.4050, 52.5200] },
  hamburg: { name: "Hamburg", center: [9.9937, 53.5511] },
  muenchen: { name: "München", center: [11.5820, 48.1351] },
  koeln: { name: "Köln", center: [6.9603, 50.9375] },
  frankfurt: { name: "Frankfurt", center: [8.6821, 50.1109] },
  duesseldorf: { name: "Düsseldorf", center: [6.7735, 51.2277] },
  stuttgart: { name: "Stuttgart", center: [9.1829, 48.7758] },
  leipzig: { name: "Leipzig", center: [12.3731, 51.3397] },
};
